import { useState, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import useGetDrivers from '../../API/hooks/drivers/useGetDrivers';
import { useDeleteDrivers } from '../../API/hooks/drivers/useDeleteDriver';
import useGetDriversLocations from '../../API/hooks/maps/useGetDriversLocations';
import { useGetDriverLocationsByIds } from '../../API/hooks/maps/useGetDriverLocations';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import Modal from '../../components/common/Modal/Modal';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import DriversItem from './DriversItem/DriversItem';
import S from './DriversPageStyles';
import ItemsList, { ItemsListRef } from '../../components/common/ItemsList/ItemsList';
import MapWidget from '../../components/common/Map/Map';
import DriversScoreWidget from '../../components/DriversScoreWidget/DriversScoreWidget';
import { AddDriver } from './Modals';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetRole from '../../API/hooks/useGetRole';
import DrivingHoursTracker from '../../components/DHT/DrivingHoursTracker';
import { FeaturesManagement } from '../../featuresControl';
import { getLocalStorageKey } from '../../utils/storageUtils';
import ScorePeriodSelector from '../../components/common/Toolbar/ScorePeriodSelector';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetDriversReport from '../../API/hooks/reports/useGetDriversReport';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import { ThemeType } from '../../theme/themeType';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import { driversPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import { useEntityScores } from '../../API/hooks/useEntityScores';
import { useDriverAlertsCounts } from '../../API/hooks/drivers/useDriverAlertsCounts';

const PERIOD_TO_STATS_LABEL: Record<string, string> = {
  daily: 'Today’s statistics',
  weekly: 'This week’s statistics',
  monthly: 'This month’s statistics',
  all: 'All-time statistics',
};

const filterOptions: FilterConfig[] = [
  {
    type: 'vehicle._id',
    display: 'radio',
    options: [
      { null: 'All' },
      { true: 'Assigned' },
      { false: 'Unassigned' },
    ],
  },
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      { distanceInKmMonthly_ASC: 'Monthly distance: asc' },
      { distanceInKmMonthly_DESC: 'Monthly distance: desc' },
      { firstName_ASC: 'Driver name: asc' },
      { firstName_DESC: 'Driver name: desc' },
      { score_ASC: 'Score: asc' },
      { score_DESC: 'Score: desc' },
      { alertCountMonthly_ASC: 'Monthly alerts: asc' },
      { alertCountMonthly_DESC: 'Monthly alerts: desc' },
    ],
  },
];

function DriversPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const fleetId = useGetFleetId();
  const { isFleetViewer } = useGetRole();
  const { data: fleetData } = useGetFleet(fleetId);

  const [addDriverModalOpen, toggleAddDriverModal] = useState(false);
  const [dhtModalOpen, toggleDHTModalOpen] = useState(false);
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const itemsListRef = useRef<ItemsListRef>(null);
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('drivers-page-filters'), driversPageFilters);
  const { startDate, endDate, ...driverFilters } = filters || driversPageFilters;
  const { data: allDriversData } = useGetDrivers(fleetId, '?limit=1000&skip=0');
  const driverIds = (allDriversData?.results ?? []).map((d) => d._id);
  const { scores: computedDriverScores, isLoading: isScoresLoading } = useEntityScores(fleetId, driverIds, 'driver');
  const { alertCounts: computedDriverAlertCounts } = useDriverAlertsCounts(fleetId, driverIds);

  const period = filters?.period?.[0];
  const isAllTime = period === 'custom' && filters?.startDate?.[0] === new Date(0).toISOString();
  const normalizedPeriod = isAllTime ? 'all' : period;
  const scoreField = ({ daily: 'scoreDaily', weekly: 'scoreWeekly', monthly: 'scoreMonthly' } as Record<string, string>)[period ?? ''] ?? 'score';
  const statsLabel = PERIOD_TO_STATS_LABEL[normalizedPeriod] ?? 'All-time statistics';

  const nullScores = {
    score: null, scoreDaily: null, scoreWeekly: null, scoreMonthly: null,
  };
  const driversWithComputedScores = (allDriversData?.results ?? []).map((d) => {
    const cs = computedDriverScores.get(d._id);
    return { ...d, ...nullScores, ...(cs ?? {}) } as typeof d;
  });

  const averageScore = useMemo<number | null>(() => {
    if (isScoresLoading || driversWithComputedScores.length === 0) return null;
    const valid = driversWithComputedScores
      .map((d) => d[scoreField as keyof typeof d] as number | null)
      .filter((s): s is number => s != null && s > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
  }, [driversWithComputedScores, scoreField, isScoresLoading]);
  const { mutate: mutateDelete } = useDeleteDrivers(
    () => {
      toast.success('Driver(s) deleted successfully');
      itemsListRef.current?.toggleSelection(false);
    },
    () => toast.error('One ore more driver(s) failed to delete'),
  );

  const handleDeleteDrivers = (ids: string[]): void => mutateDelete({
    fleetId,
    ids,
  });

  const handleDeleteItems = (): void => {
    handleDeleteDrivers(selectedItems);
    toggleDeleteModalOpen(false);
  };

  const BUTTONS = [
    {
      title: 'DHT Settings',
      action: () => toggleDHTModalOpen(true),
      hide: !FeaturesManagement.features.drivingHoursTracker,
    },
    {
      title: 'Add new driver',
      iconKey: IconKeys.PLUS,
      action: () => toggleAddDriverModal(true),
      hide: isFleetViewer,
    },
  ];

  const LIST_BUTTONS = [
    {
      title: `Delete${selectedItems.length > 0 ? ` ${selectedItems.length} driver(s)` : ''}`,
      action: () => toggleDeleteModalOpen(true),
      hide: isFleetViewer,
      height: '25px',
      background: theme.colors.red,
      hoverBackground: theme.colors.red,
      disabled: selectedItems.length < 1,
    },
  ];

  const reportName = `${fleetData?.name || 'fleet_na'}_drivers`;

  return (
    <>
      <S.Wrapper>
        <Toolbar title="Drivers" buttons={BUTTONS} />
        <div className="sub-header">
          <ScorePeriodSelector filters={filters} setFilters={setFilters} />
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
        <div className="widgets-container">
          <MapWidget
            fleetId={fleetId}
            entityType="driver"
            getLocationsQuery={useGetDriversLocations}
            getLocationDetailsQuery={useGetDriverLocationsByIds}
            groupFilters={groupFilters || {}}
            mapWrapperWidth="80%"
          />
          <DriversScoreWidget
            fleetId={fleetId}
            reportName={reportName}
            groupFilters={groupFilters || {}}
            averageScore={averageScore}
            period={normalizedPeriod}
            allDrivers={driversWithComputedScores}
          />
        </div>
        <ItemsList
          ref={itemsListRef}
          entity="drivers"
          title={statsLabel}
          fleetId={fleetId}
          filtersConfig={filterOptions}
          filters={driverFilters}
          defaultFilters={driversPageFilters}
          setFilters={setFilters}
          groupFilters={groupFilters || {}}
          getQuery={useGetDrivers}
          hasSearch
          hasCreateReport
          reportEndpoint={useGetDriversReport}
          reportName={reportName}
          buttons={LIST_BUTTONS}
          onSelect={setSelectedItems}
          curentPage={useGetCurrentPage()}
          scoreOverrides={computedDriverScores}
          alertCountOverrides={computedDriverAlertCounts}
        >
          <DriversItem />
        </ItemsList>
      </S.Wrapper>
      {addDriverModalOpen && (
        <Modal
          title="Add new driver"
          state={addDriverModalOpen}
          toggleState={toggleAddDriverModal}
        >
          <AddDriver fleetId={fleetId} toggleModal={toggleAddDriverModal} />
        </Modal>
      )}
      {dhtModalOpen && fleetId && (
        <Modal
          title="Driving Hours Tracker - Settings"
          state={dhtModalOpen}
          settings={{ maxWidth: '500px', minWidth: '500px' }}
          toggleState={toggleDHTModalOpen}
        >
          <DrivingHoursTracker readonly={isFleetViewer} fleetId={fleetId} toggleModal={toggleDHTModalOpen} />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete these driver(s)"
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteItems}
          />
        </Modal>
      )}
    </>
  );
}

export default DriversPage;
